/**
 * Ersetzt cid:-Bildquellen in E-Mail-HTML durch authentifizierte Download-URLs,
 * damit Signaturen/Logos im Lead-Verlauf angezeigt werden.
 */

export type InlineMailAttachment = {
  id?: string | null;
  filename?: string | null;
  content_type?: string | null;
  content_id?: string | null;
};

export type RewriteInlineImagesContext = {
  accountId: string | number;
  messageId: string;
  attachments: InlineMailAttachment[];
};

export function parseAttachmentsJson(raw: unknown): InlineMailAttachment[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw as InlineMailAttachment[];
  if (typeof raw === "string") {
    try {
      const p = JSON.parse(raw) as unknown;
      return Array.isArray(p) ? (p as InlineMailAttachment[]) : [];
    } catch {
      return [];
    }
  }
  return [];
}

function normalizeCidRef(raw: string): string {
  let s = raw.trim().replace(/^cid:/i, '').replace(/^<|>$/g, '').trim();
  try {
    s = decodeURIComponent(s);
  } catch {
    /* ignore */
  }
  return s.toLowerCase();
}

function contentIdMatches(refNorm: string, attCid: string | null | undefined): boolean {
  if (!attCid || !refNorm) return false;
  const a = normalizeCidRef(attCid);
  return a === refNorm || refNorm.includes(a) || a.includes(refNorm);
}

function filenameHintFromCidSrc(src: string): string | null {
  const m = /^cid:\s*(.+)$/i.exec(src.trim());
  if (!m) return null;
  let inner = m[1].trim().replace(/^<|>$/g, '');
  const at = inner.indexOf('@');
  if (at > 0) inner = inner.slice(0, at);
  const hint = inner.trim();
  return hint || null;
}

function buildDownloadUrl(ctx: RewriteInlineImagesContext, att: InlineMailAttachment): string | null {
  const aid = String(att.id ?? '').trim();
  if (!aid) return null;
  const fn = (att.filename || 'inline').replace(/"/g, '');
  const base = `/api/inbox/accounts/${encodeURIComponent(String(ctx.accountId))}/attachments/download`;
  const p = new URLSearchParams();
  p.set('message_id', ctx.messageId);
  p.set('attachment_id', aid);
  p.set('filename', fn);
  p.set('inline', '1');
  return `${base}?${p.toString()}`;
}

function pickAttachmentForCid(src: string, attachments: InlineMailAttachment[]): InlineMailAttachment | undefined {
  const refNorm = normalizeCidRef(src.replace(/^cid:/i, ''));
  if (!refNorm) return undefined;

  for (const att of attachments) {
    if (att.content_id && contentIdMatches(refNorm, att.content_id)) return att;
  }

  const hint = filenameHintFromCidSrc(src);
  if (hint) {
    const h = hint.toLowerCase();
    const byName = attachments.find((att) => {
      const fn = (att.filename || '').toLowerCase();
      return fn && (fn === h || fn.endsWith(h) || h.endsWith(fn) || refNorm.includes(fn.replace(/\s/g, '')));
    });
    if (byName?.id) return byName;
  }

  return undefined;
}

/**
 * Nur im Browser sinnvoll (DOMParser). Server-seitig wird der HTML-String unverändert zurückgegeben.
 */
export function rewriteEmailInlineImages(html: string, ctx: RewriteInlineImagesContext | null): string {
  if (!html || !ctx?.messageId || !ctx.accountId) return html;
  if (typeof window === 'undefined') return html;
  if (!html.toLowerCase().includes('cid:')) return html;

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const imgs = doc.querySelectorAll('img');
  let changed = false;
  imgs.forEach((img) => {
    const src = img.getAttribute('src') || '';
    if (!/^cid:/i.test(src.trim())) return;
    const att = pickAttachmentForCid(src, ctx.attachments);
    if (!att) return;
    const url = buildDownloadUrl(ctx, att);
    if (!url) return;
    img.setAttribute('src', url);
    changed = true;
  });

  return changed ? doc.body.innerHTML : html;
}
